<main>
  <loading-overlay [isLoading]="isLoading() > 0" />

  <div class="content">
    <div class="page__title">
      <div class="page__title_container">
        <div class="flex items-center gap-2">
          <h1>{{ 'AccessCatalog_Title' | translateWF: 'Access catalog' }}</h1>
          <i
            class="pi pi-info-circle text-color-secondary"
            tooltipPosition="bottom"
            [pTooltip]="
              'AccessCatalog_Note'
                | translateWF
                  : 'Role descriptions and the pages of other apps come from UserMaster.API\'s catalog file, this app\'s pages from its routes and endpoints from the API\'s code. Groups come from the data, so every group is listed. UserMaster and Production roles are described so far.'
            "
          ></i>
        </div>
      </div>
    </div>

    <p>{{ 'AccessCatalog_Subtitle' | translateWF: 'What each group allows, through its roles, in UserMaster and the other apps.' }}</p>

    <div class="grid grid-cols-12 gap-[2rem]">
      <div class="col-span-12 md:col-span-3">
        @if (filterConfig(); as config) {
          <csl-dynamic-filters [filterConfig]="config" [state]="filterState" />
        }
        <div class="flex flex-col gap-2 mt-4 max-h-[24rem] overflow-y-auto">
          @for (missing of describedButMissing(); track missing.name) {
            <p-message severity="warn" icon="pi pi-exclamation-triangle" styleClass="text-sm">
              <span>
                {{ 'AccessCatalog_Drift_RoleMissing' | translateWF: 'Described in the catalog, but no role has this name:' }}
                <code class="block break-all">{{ missing.name }}</code>
              </span>
            </p-message>
          }
          @for (entry of missingEndpoints(); track entry.role + entry.action.method + entry.action.route) {
            <p-message severity="error" icon="pi pi-times-circle" styleClass="text-sm">
              <span>
                {{ 'AccessCatalog_Drift_EndpointMissing' | translateWF: "The catalog names an endpoint UserMaster.API doesn't have:" }}
                <code class="block break-all">{{ entry.role }}: {{ entry.action.method }} {{ entry.action.route }}</code>
              </span>
            </p-message>
          }
          @if (undescribedCount() > 0) {
            <p-message severity="secondary" icon="pi pi-info-circle" styleClass="text-sm">
              {{ undescribedCount() }}
              {{ 'AccessCatalog_Drift_Undescribed' | translateWF: "roles aren't described yet (other domains come from the Discovery)." }}
            </p-message>
          }
        </div>
      </div>

      <div class="col-span-12 md:col-span-9 flex flex-col gap-4 min-w-0">
        <p-tabs value="groups">
          <p-tablist>
            <p-tab value="groups">{{ 'AccessCatalog_Tab_Groups' | translateWF: 'Groups' }} ({{ filteredGroups().length }})</p-tab>
            <p-tab value="endpoints">{{ 'AccessCatalog_Tab_Endpoints' | translateWF: 'Endpoints' }} ({{ filteredEndpoints().length }})</p-tab>
          </p-tablist>
          <p-tabpanels>
            <!-- GROUPS -->
            <p-tabpanel value="groups">
              <p-table [value]="filteredGroups()" dataKey="group.id" [rowHover]="true" [paginator]="true" [rows]="25" [rowsPerPageOptions]="[25, 50, 100]">
                <ng-template #header>
                  <tr>
                    <th class="w-[3rem]"></th>
                    <th>{{ 'AccessCatalog_Group' | translateWF: 'Group' }}</th>
                    <th>{{ 'UserDetails_Table_Type' | translateWF: 'Type' }}</th>
                    <th>{{ 'UserDetails_Table_ExternalManagers' | translateWF: 'External managers' }}</th>
                    <th class="text-right">{{ 'AccessCatalog_Roles' | translateWF: 'Roles' }}</th>
                  </tr>
                </ng-template>
                <ng-template #body let-row let-expanded="expanded">
                  @let entry = catalogGroup(row);
                  <tr>
                    <td>
                      <p-button type="button" [pRowToggler]="row" [text]="true" [rounded]="true" [icon]="expanded ? 'pi pi-chevron-down' : 'pi pi-chevron-right'" />
                    </td>
                    <td>
                      {{ entry.group.name }}
                      @if (entry.group.description) {
                        <div class="text-sm text-color-secondary">{{ entry.group.description }}</div>
                      }
                      @if (entry.summary.length) {
                        <div class="text-sm mt-1">
                          <span class="font-medium">{{ 'AccessCatalog_CanDo' | translateWF: 'Can' }}:</span>
                          {{ entry.summary.join(' · ') }}
                        </div>
                      }
                      @if (entry.matchedRoles.length) {
                        <div class="flex flex-wrap items-center gap-1 text-sm mt-1">
                          <span class="font-medium">{{ 'AccessCatalog_Through' | translateWF: 'Through' }}:</span>
                          @for (roleName of entry.matchedRoles; track roleName) {
                            <p-tag severity="success" [value]="roleName" />
                          }
                        </div>
                      }
                    </td>
                    <td>{{ typeLabel(entry.group.type).key | translateWF: typeLabel(entry.group.type).fallback }}</td>
                    <td>
                      @switch (entry.visibility) {
                        @case ('global') {
                          {{ 'UserDetails_ExternalManagers_NotAllowed' | translateWF: 'Not allowed (Global)' }}
                        }
                        @case ('visible') {
                          <p-tag severity="success" [value]="'UserDetails_ExternalManagers_Visible' | translateWF: 'Visible'" />
                        }
                        @default {
                          <p-tag severity="secondary" [value]="'UserDetails_ExternalManagers_Hidden' | translateWF: 'Hidden'" />
                        }
                      }
                    </td>
                    <td class="text-right tabular-nums">{{ entry.roles.length }}</td>
                  </tr>
                </ng-template>
                <ng-template #expandedrow let-row>
                  @let entry = catalogGroup(row);
                  <tr>
                    <td colspan="5">
                      <p-table [value]="entry.roles">
                        <ng-template #header>
                          <tr>
                            <th>{{ 'AccessCatalog_Role' | translateWF: 'Role' }}</th>
                            <th>{{ 'AccessCatalog_Pages' | translateWF: 'Pages' }}</th>
                            <th>{{ 'AccessCatalog_Allows' | translateWF: 'What it allows' }}</th>
                          </tr>
                        </ng-template>
                        <ng-template #body let-catalogRole>
                          <tr>
                            <td class="align-top">
                              <div class="flex items-center gap-2">
                                {{ catalogRole.role.name }}
                                @if (catalogRole.description?.domain) {
                                  <p-tag severity="secondary" [value]="catalogRole.description.domain" />
                                }
                              </div>
                              @if (catalogRole.description?.description || catalogRole.role.description) {
                                <div class="text-sm text-color-secondary">{{ catalogRole.description?.description || catalogRole.role.description }}</div>
                              }
                            </td>
                            <td class="align-top">
                              <div class="flex flex-wrap gap-1">
                                @for (page of catalogRole.pages; track page.path) {
                                  <a
                                    [routerLink]="page.path"
                                    [pTooltip]="page.labelKey ? (page.labelKey | translateWF: page.fallbackLabel) : page.fallbackLabel"
                                    tooltipPosition="top"
                                  >
                                    <p-tag severity="info" styleClass="cursor-pointer font-mono" [value]="page.path" />
                                  </a>
                                }
                                @for (page of catalogRole.externalPages; track page.app + page.path) {
                                  <p-tag severity="secondary" styleClass="font-mono" [value]="page.app + ' ' + page.path" [pTooltip]="page.label" tooltipPosition="top" />
                                }
                                @if (!catalogRole.pages.length && !catalogRole.externalPages.length) {
                                  —
                                }
                              </div>
                            </td>
                            <td class="align-top">
                              @if (catalogRole.description) {
                                @for (action of catalogRole.description.actions; track action.method + action.route) {
                                  @let check = checkLabel(action);
                                  <div class="flex flex-wrap items-center gap-2 mb-2">
                                    <span>{{ action.does }}</span>
                                    <p-tag [severity]="check.severity" [value]="check.key | translateWF: check.fallback" />
                                    <code class="text-xs text-color-secondary">{{ action.method }} {{ action.route }}</code>
                                  </div>
                                } @empty {
                                  @if (catalogRole.pages.length || catalogRole.externalPages.length) {
                                    {{ 'AccessCatalog_PagesOnly' | translateWF: 'Pages only, no API actions' }}
                                  } @else {
                                    {{ 'AccessCatalog_NoActions' | translateWF: 'No API actions' }}
                                  }
                                }
                              } @else {
                                @for (endpoint of catalogRole.endpoints; track endpoint.method + endpoint.route) {
                                  <div class="flex items-center gap-2 mb-1">
                                    <p-tag [severity]="methodSeverity(endpoint.method)" [value]="endpoint.method" />
                                    <code>{{ endpoint.route }}</code>
                                  </div>
                                } @empty {
                                  <span class="text-color-secondary">{{ 'AccessCatalog_NotDescribed' | translateWF: 'Not described yet' }}</span>
                                }
                              }
                            </td>
                          </tr>
                        </ng-template>
                        <ng-template #emptymessage>
                          <tr>
                            <td colspan="3">{{ 'AccessCatalog_NoRoles' | translateWF: 'This group has no roles' }}</td>
                          </tr>
                        </ng-template>
                      </p-table>
                    </td>
                  </tr>
                </ng-template>
                <ng-template #emptymessage>
                  <tr>
                    <td colspan="5">{{ 'AccessCatalog_NoGroups' | translateWF: 'No groups found' }}</td>
                  </tr>
                </ng-template>
              </p-table>
            </p-tabpanel>

            <!-- ENDPOINTS -->
            <p-tabpanel value="endpoints">
              <p-table [value]="filteredEndpoints()" [rowHover]="true" [paginator]="true" [rows]="50" [rowsPerPageOptions]="[50, 100, 200]">
                <ng-template #header>
                  <tr>
                    <th>{{ 'AccessCatalog_Method' | translateWF: 'Method' }}</th>
                    <th>{{ 'AccessCatalog_Route' | translateWF: 'Route' }}</th>
                    <th>{{ 'AccessCatalog_RequiredRoles' | translateWF: 'Required roles' }}</th>
                    <th>{{ 'AccessCatalog_Delegates' | translateWF: 'Delegated managers' }}</th>
                  </tr>
                </ng-template>
                <ng-template #body let-row>
                  @let endpoint = endpointRow(row);
                  <tr>
                    <td><p-tag [severity]="methodSeverity(endpoint.method)" [value]="endpoint.method" /></td>
                    <td>
                      <code>{{ endpoint.route }}</code>
                    </td>
                    <td>
                      @if (!endpoint.requiresLogin) {
                        <p-tag severity="warn" [value]="'AccessCatalog_Public' | translateWF: 'Public (no login)'" />
                      } @else if (endpoint.requiredRoles.length === 0) {
                        <span class="text-color-secondary">{{ 'AccessCatalog_AnyUser' | translateWF: 'Any logged-in user' }}</span>
                      } @else {
                        {{ rolesLabel(endpoint) }}
                      }
                    </td>
                    <td>
                      @if (endpoint.forbidsDelegate) {
                        <p-tag severity="danger" [value]="'AccessCatalog_Refused' | translateWF: 'Refused'" />
                      } @else {
                        —
                      }
                    </td>
                  </tr>
                </ng-template>
                <ng-template #emptymessage>
                  <tr>
                    <td colspan="4">{{ 'AccessCatalog_NoEndpoints' | translateWF: 'No endpoints found' }}</td>
                  </tr>
                </ng-template>
              </p-table>
            </p-tabpanel>
          </p-tabpanels>
        </p-tabs>
      </div>
    </div>
  </div>
</main>
